import React, { useEffect, useMemo, useState } from 'react';

export default function WooLoyalty() {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [byChannel, setByChannel] = useState(true);
  const [channelRows, setChannelRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const refresh = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setSummary(null); return; }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (!byChannel) {
        const url = params.toString()
          ? `${base}/api/woocommerce/loyalty/summary?${params.toString()}`
          : `${base}/api/woocommerce/loyalty/summary`;
        const res = await fetch(url, { credentials: 'include', headers });
        const js = await res.json().catch(() => ({}));
        if (res.ok && js?.ok) { setSummary(js); setChannelRows([]); }
        else { setSummary(null); setChannelRows([]); }
      } else {
        const url = params.toString()
          ? `${base}/api/woocommerce/loyalty/by-channel?${params.toString()}`
          : `${base}/api/woocommerce/loyalty/by-channel`;
        const res = await fetch(url, { credentials: 'include', headers });
        const js = await res.json().catch(() => ({}));
        if (res.ok && js?.ok) { setChannelRows(js.rows || []); setSummary({ period: js.period }); }
        else { setChannelRows([]); }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodText = useMemo(() => {
    const s = summary?.period?.startDate || startDate;
    const e = summary?.period?.endDate || endDate;
    if (s && e) return `${s} → ${e}`;
    if (s && !e) return `${s} → (today)`;
    if (!s && e) return `(all time) → ${e}`;
    return 'All time';
  }, [summary, startDate, endDate]);

  const segments = useMemo(() => {
    const s = summary?.summary || {};
    const totalRevenue = Number(s.total_revenue || 0);
    const totalCustomers = Number(s.total_customers || 0);
    const build = (key, title, count, rev, ords) => {
      const customers = Number(count || 0);
      const revenue = Number(rev || 0);
      const orders = Number(ords || 0);
      const share = totalRevenue > 0 ? (revenue / totalRevenue) : 0;
      const avgRev = customers > 0 ? (revenue / customers) : 0;
      const avgOrders = customers > 0 ? (orders / customers) : 0;
      const customerShare = totalCustomers > 0 ? (customers / totalCustomers) : 0;
      return { key, title, customers, revenue, orders, share, customerShare, avgRev, avgOrders };
    };
    return [
      build('champion', 'Champions (8+)', s.champion, s.revenue_champion, s.orders_champion),
      build('loyal', 'Loyal (4–7)', s.loyal, s.revenue_loyal, s.orders_loyal),
      build('returning', 'Returning (2–3)', s.returning, s.revenue_returning, s.orders_returning),
      build('one_time', 'Explorers (1)', s.one_time, s.revenue_one_time, s.orders_one_time),
    ];
  }, [summary]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/woocommercelogo.webp" alt="WooCommerce" className="h-8 object-contain" />
            <h1 className="text-xl font-bold text-gray-900">Loyalty Segments</h1>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-sm border rounded-lg"
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-sm border rounded-lg"
            />
            <button
              onClick={refresh}
              className={`px-3 py-1.5 text-sm rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition-colors ${loading ? 'opacity-50' : ''}`}
              disabled={loading}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Period */}
      <div className="text-sm text-gray-600">
        Period: <span className="font-medium text-gray-800">{periodText}</span>
      </div>

      {/* Segments (stacked) or by-channel list */}
      <div className="space-y-4">
        {!byChannel && segments.map((seg) => (
          <div key={seg.key} className="rounded-2xl border shadow-sm overflow-hidden">
            <div className="bg-black text-white p-4">
              <div className="text-lg font-semibold">{seg.title}</div>
              <div className="text-xs opacity-90">
                Customers: {seg.customers.toLocaleString()} • Revenue Share: {Math.round(seg.share * 100).toString()}% • Customer Share: {Math.round(seg.customerShare * 100).toString()}%
              </div>
            </div>
            <div className="bg-white p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-500">Total Revenue</div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(seg.revenue).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Revenue per Customer</div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(seg.avgRev).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Orders</div>
                <div className="text-lg font-semibold text-gray-900">{seg.orders.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Orders per Customer</div>
                <div className="text-lg font-semibold text-gray-900">
                  {Math.round(seg.avgOrders).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        ))}
        {byChannel && channelRows.map((row) => {
          const totalRevenue = Number(row.total_revenue || 0);
          const totalCustomers = Number(row.total_customers || 0);
          const mkSeg = (title, cnt, rev, ords) => {
            const customers = Number(cnt || 0);
            const revenue = Number(rev || 0);
            const orders = Number(ords || 0);
            const share = totalRevenue > 0 ? (revenue / totalRevenue) : 0;
            const customerShare = totalCustomers > 0 ? (customers / totalCustomers) : 0;
            const avgRev = customers > 0 ? revenue / customers : 0;
            const avgOrders = customers > 0 ? orders / customers : 0;
            return { title, customers, revenue, orders, share, customerShare, avgRev, avgOrders };
          };
          const channelSegs = [
            mkSeg('Champions (8+)', row.champion, row.revenue_champion, row.orders_champion),
            mkSeg('Loyal (4–7)', row.loyal, row.revenue_loyal, row.orders_loyal),
            mkSeg('Returning (2–3)', row.returning, row.revenue_returning, row.orders_returning),
            mkSeg('Explorers (1)', row.one_time, row.revenue_one_time, row.orders_one_time),
          ];
          return (
            <div key={row.sales_channel_name} className="rounded-2xl border shadow-sm overflow-hidden">
              <div className="bg-black text-white p-4">
                <div className="text-lg font-semibold">{row.sales_channel_name}</div>
                <div className="text-xs opacity-90">
                  Customers: {Number(row.total_customers || 0).toLocaleString()} • Revenue: {Math.round(totalRevenue).toLocaleString()}
                </div>
              </div>
              <div className="bg-white p-4 space-y-3">
                {channelSegs.map((seg) => (
                  <div key={seg.title} className="border rounded-xl">
                    <div className="px-4 py-2 border-b font-semibold text-gray-900">{seg.title}</div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Customers</div>
                        <div className="text-lg font-semibold text-gray-900">{seg.customers.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Total Revenue</div>
                        <div className="text-lg font-semibold text-gray-900">{Math.round(seg.revenue).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Revenue per Customer</div>
                        <div className="text-lg font-semibold text-gray-900">{Math.round(seg.avgRev).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Orders per Customer</div>
                        <div className="text-lg font-semibold text-gray-900">{Math.round(seg.avgOrders).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


